import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {FormFieldT, FormSelectOptionsT} from '@/lib/types/form.types';
import {retailerInsertT, retailerT} from '@/lib/types/retailer.types';
import {statsRowT} from '@/lib/types/stats.types';

import {createDataTableConfig} from '@/lib/helpers/config';
import {US_STATES_DATA} from '@/lib/jsonData/usStatesData';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useRetailers} from '@/providers/components/retailersProvider';

import {ConfirmDeleteModalComponent, ModalFormComponent, StatsRowComponent} from '@/components';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';

// form data for creating a new retailer
const initialFormData: retailerInsertT = {
  created_by: '',
  date_created: '',
  date_modified: '',
  retailer_address: '',
  retailer_address2: '',
  retailer_city: '',
  retailer_email: '',
  retailer_geo_points: null,
  retailer_license_number: '',
  retailer_name: '',
  retailer_phone: '',
  retailer_postal_code: '',
  retailer_state: '',
};

export const shareCustomerProfileOptions: FormSelectOptionsT[] = [
  {value: 'true', label: 'Yes'},
  {value: 'false', label: 'No'},
];

const createFields: FormFieldT<retailerT>[] = [
  {name: 'retailer_name', label: 'Retailer Name', type: 'text', placeholder: 'Retailer Name', required: true},
  {name: 'retailer_address', label: 'Address', type: 'text', placeholder: 'Address', required: true},
  {name: 'retailer_address2', label: 'Address 2', type: 'text', placeholder: 'Address 2', required: false}, // Optional field
  {name: 'retailer_city', label: 'City', type: 'text', placeholder: 'City', required: true},
  {name: 'retailer_state', label: 'State', type: 'select', selectOptions: US_STATES_DATA, required: true},
  {name: 'retailer_postal_code', label: 'Postal Code', type: 'text', placeholder: 'Postal Code', required: true},
  {
    name: 'retailer_license_number',
    label: 'License Number',
    type: 'text',
    placeholder: 'License Number',
    required: true,
  },
  {
    name: 'retailer_geo_points',
    label: 'Geo Points',
    type: 'text',
    placeholder: 'Geo Points',
    required: false,
  },
];

const editFields: FormFieldT<retailerT>[] = [...createFields];

function AdminRetailers() {
  const {isAdministrator} = useAuth();
  const {createData, updateData, deleteData, getRetailer} = useRetailers();

  // State
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Refs
  const selectedRetailerIdRef = useRef<number | null>(null);
  const formDataRef = useRef<retailerInsertT>(initialFormData);
  // Reactivity
  const {data: stats, refetch: refetchStats} = useViewQuery({
    view: 'retailer_stats',
  });

  // Functions
  const handleCreate = async (data: retailerT) => {
    try {
      setIsSubmitting(true);
      await createData(data);
      refetchStats();
      setIsVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to create retailer ${err}`);
    } finally {
      setSubmissionError(null);
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (data: retailerT) => {
    if (selectedRetailerIdRef.current === null) {
      setSubmissionError('No retailer selected');
      return;
    }

    try {
      setIsSubmitting(true);
      await updateData(data);
      refetchStats();
      setIsVisible(false);
    } catch (err) {
      setSubmissionError(`Failed to update retailer: ${err}`);
    } finally {
      setSubmissionError(null);
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (selectedRetailerIdRef.current === null) {
      setDeleteError('No speculator selected');
      return;
    }

    try {
      setIsSubmitting(true);
      await deleteData(selectedRetailerIdRef.current);
      refetchStats();
      setIsDeleteModalVisible(false);
    } catch (err) {
      setDeleteError(`Failed to delete user ${err}`);
    } finally {
      setDeleteError(null);
      setIsSubmitting(false);
    }
  };

  const handleAdd = async () => {
    if (!isAdministrator) return;
    formDataRef.current = initialFormData;
    setIsEditing(false);
    setIsVisible(true);
  };

  const handleEdit = async (retailerId: number) => {
    const retailer = await getRetailer(retailerId);
    formDataRef.current = retailer;
    selectedRetailerIdRef.current = retailer.retailer_id;
    setIsEditing(true);
    setIsVisible(true);
  };

  const handleDeleteEvent = (retailerId: number) => {
    selectedRetailerIdRef.current = retailerId;
    setIsDeleteModalVisible(true);
  };

  // Stats
  const statsRow: statsRowT = [
    {
      title: 'Total Retailers',
      value: stats?.total ?? 0,
      subtitle: 'Across ALL Retailers',
      icon: 'ri-store-line',
      color: 'primary',
      cardClass: 'Retailers-main-card primary',
    },
    {
      title: 'Active Retailers',
      value: stats?.active ?? 0,
      subtitle: 'Across ALL Retailers',
      icon: 'ri-check-line',
      color: 'secondary',
      cardClass: 'Retailers-main-card secondary',
    },
    {
      title: 'Inactive Retailers',
      value: stats?.inactive ?? 0,
      subtitle: 'Across ALL Retailers',
      icon: 'ri-close-line',
      color: 'warning',
      cardClass: 'Retailers-main-card warning',
    },
  ];

  // Data Table
  const dataTableConfig = createDataTableConfig({
    table: 'Retailer',
    title: 'Retailer Summary',
    searchColumn: 'retailer_name',
    columns: [
      'retailer_id',
      'retailer_name',
      'retailer_address',
      'retailer_city',
      'retailer_state',
      'retailer_postal_code',
      'retailer_license_number',
      'date_removed',
    ],
    onAdd: handleAdd,
    columnsDisplay: [
      {
        header: 'Status',
        field: 'retailer_id',
        render: retailer => (
          <span className={`badge bg-${!retailer.date_removed ? 'success' : 'danger'} text-white`}>
            {!retailer.date_removed ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Retailer Name',
        field: 'retailer_name',
        render: retailer => (
          <Link className="text-primary underline" to={`/retailers/${retailer.retailer_id}`}>
            {retailer.retailer_name}
          </Link>
        ),
      },
      {header: 'Address', field: 'retailer_address'},
      {header: 'City', field: 'retailer_city'},
      {header: 'State', field: 'retailer_state'},
      {header: 'Postal Code', field: 'retailer_postal_code'},
      {header: 'License Number', field: 'retailer_license_number'},
    ],
    actionsDisplay: [
      {
        icon: 'ri-pencil-line',
        color: 'info',
        handler: retailer => handleEdit(retailer.retailer_id),
        permission: () => isAdministrator,
      },
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: retailer => handleDeleteEvent(retailer.retailer_id),
        permission: () => isAdministrator,
      },
    ],
  });

  return (
    <Page currentPage="Retailers">
      <StatsRowComponent stats={statsRow} />
      <DataTable {...dataTableConfig} />
      <ModalFormComponent
        isVisible={isVisible}
        fields={isEditing ? editFields : createFields}
        formData={formDataRef.current}
        onSubmit={data => {
          setSubmissionError(null);
          if (isEditing) {
            handleUpdate(data);
          } else {
            handleCreate(data);
          }
        }}
        onClose={() => {
          setSubmissionError(null);
          setIsVisible(false);
        }}
        error={submissionError} // Pass the error to the modal
        isSubmitting={isSubmitting} // Pass the loading state to the modal
        title={isEditing ? 'Edit Retailer' : 'Create New Retailer'}
      />
      <ConfirmDeleteModalComponent
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDelete}
        title="Confirm Deletion"
        description="Are you sure you want to delete this retailer? This action cannot be undone."
        isSubmitting={isSubmitting}
        error={deleteError}
      />
    </Page>
  );
}

export default AdminRetailers;
